import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTheme } from '@mui/material/styles';

import Grid from '@mui/material/Grid';
import { Typography, Card, CardContent, Box, Paper, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import Main from 'layouts/Main';

const StudentCard = styled(Card)(({ theme }) => ({
  textAlign: 'center',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-12px)',
    boxShadow:
      theme.palette.mode === 'dark'
        ? '0 20px 40px rgba(74, 105, 255, 0.4)'
        : '0 20px 40px rgba(74, 105, 255, 0.25)',
  },
  backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#ffffff',
  borderRadius: '20px',
  overflow: 'visible',
  position: 'relative',
  border:
    theme.palette.mode === 'dark'
      ? '2px solid rgba(100, 150, 200, 0.4)'
      : '2px solid rgba(100, 150, 200, 0.5)',
}));

const ImageWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  paddingTop: '120%',
  overflow: 'hidden',
  borderRadius: '12px',
  margin: '12px',
  background: `linear-gradient(135deg, ${
    theme.palette.mode === 'dark'
      ? 'rgba(255,255,255,0.05)'
      : 'rgba(100,150,200,0.05)'
  } 0%, ${
    theme.palette.mode === 'dark'
      ? 'rgba(100,150,200,0.1)'
      : 'rgba(200,220,255,0.1)'
  } 100%)`,
  border:
    theme.palette.mode === 'dark'
      ? '2px solid rgba(100, 150, 200, 0.4)'
      : '2px solid rgba(100, 150, 200, 0.3)',
  '& img': {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
}));

const HeaderSection = styled(Paper)(({ theme }) => ({
  background:
    theme.palette.mode === 'dark'
      ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)'
      : 'linear-gradient(135deg, #c5e1ff 0%, #e8d5f2 50%, #d0f0ff 100%)',
  padding: '50px 20px',
  marginBottom: '50px',
  borderRadius: 0,
  boxShadow:
    theme.palette.mode === 'dark'
      ? '0 8px 32px rgba(0, 0, 0, 0.6)'
      : '0 8px 32px rgba(0, 0, 0, 0.12)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
      theme.palette.mode === 'dark'
        ? 'radial-gradient(circle at 20% 50%, rgba(100, 150, 200, 0.1) 0%, transparent 50%)'
        : 'radial-gradient(circle at 20% 50%, rgba(100, 150, 200, 0.15) 0%, transparent 50%)',
    pointerEvents: 'none',
  },
}));

const JeeAdvancedResults2026 = () => {
  const theme = useTheme();
  const [config, setConfig] = useState({ base_image_url_2025: '' });

  useEffect(() => {
    fetch('/config.json')
      .then((response) => response.json())
      .then((data) => setConfig(data))
      .catch((error) => console.error('Error fetching config:', error));
  }, []);

  const baseUrl = config.base_image_url_2025;

  const students = [
    {
      id: 1,
      name: 'M SHIFAN KHAN',
      air: 1575,
      image: `${baseUrl}/home/achievements2026/jee-toppers-2026/1.webp`,
    },
    {
      id: 2,
      name: 'KAUSHAL SURESH',
      air: 10684,
      image: `${baseUrl}/home/achievements2026/jee-toppers-2026/2.webp`,
    },
    {
      id: 3,
      name: 'RANVIR CHOUDHARY',
      air: 15275,
      image: `${baseUrl}/home/achievements2026/jee-toppers-2026/3.webp`,
    },
  ];

  const topStudent = students[0];
  const otherStudents = students.slice(1);

  const renderStudentCard = (student) => (
    <Grid
      item
      xs={12}
      sm={6}
      md={4}
      key={student.id}
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
      }}
    >
      <StudentCard sx={{ width: '100%', maxWidth: '240px' }}>
        <ImageWrapper>
          <img src={student.image} alt={student.name} loading="lazy" />
        </ImageWrapper>
        <CardContent sx={{ pb: 2.5, pt: 2.5, px: 2, textAlign: 'center' }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 800,
              mb: 1,
              fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.95rem' },
              color: theme.palette.mode === 'dark' ? '#fff' : '#1a1a2e',
              lineHeight: 1.2,
              letterSpacing: '0.5px',
            }}
          >
            {student.name}
          </Typography>
          <Typography
            sx={{
              fontWeight: 900,
              color: theme.palette.mode === 'dark' ? '#ff8a8a' : '#d32f2f',
              fontSize: { xs: '0.95rem', sm: '1.05rem' },
              lineHeight: 1.2,
            }}
          >
            AIR {student.air}
          </Typography>
        </CardContent>
      </StudentCard>
    </Grid>
  );

  return (
    <Main>
      <Helmet>
        <title>NPS YPR JEE ADVANCED ACHIEVERS 2025-26</title>
      </Helmet>
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: theme.palette.mode === 'dark' ? '#0a0a0a' : '#fafafa',
        }}
      >
        <HeaderSection elevation={0}>
          <Box
            sx={{ textAlign: 'center', mb: 2, position: 'relative', zIndex: 1 }}
          >
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                mb: 2,
                color: theme.palette.primary.main,
                textTransform: 'uppercase',
                letterSpacing: '1.5px',
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.4rem' },
                textShadow:
                  theme.palette.mode === 'dark'
                    ? '0 2px 4px rgba(0,0,0,0.3)'
                    : 'none',
              }}
            >
              JEE Advanced Achievers 2025-26
            </Typography>
          </Box>
          <Box sx={{ px: { xs: 2, sm: 3, md: 6 }, mb: 3 }}>
            <Typography
              variant="body1"
              sx={{
                maxWidth: '980px',
                mx: 'auto',
                color: theme.palette.mode === 'dark' ? '#e0e0e0' : '#333',
                fontSize: { xs: '0.98rem', sm: '1.05rem', md: '1.12rem' },
                lineHeight: 1.8,
                textAlign: { xs: 'left', md: 'center' },
              }}
            >
              With great pride and admiration, we congratulate our students on
              their exceptional performance in{' '}
              <strong>JEE Advanced 2026</strong>. Their exceptional performance
              is a testament to their unwavering determination and hard work
              towards academic excellence and reflects the steadfast support of
              the teachers and parents.
            </Typography>
          </Box>
          <Box sx={{ px: { xs: 2, sm: 3, md: 6 } }}>
            <Typography
              variant="body1"
              sx={{
                maxWidth: '980px',
                mx: 'auto',
                color: theme.palette.mode === 'dark' ? '#e0e0e0' : '#333',
                fontSize: { xs: '0.98rem', sm: '1.05rem', md: '1.12rem' },
                lineHeight: 1.8,
                textAlign: { xs: 'left', md: 'center' },
              }}
            >
              We wish them continued success as they embark on the next chapter
              of their academic journey and look forward to witnessing their
              future accomplishments.
            </Typography>
          </Box>
          <Box sx={{ mt: 4, textAlign: 'center', position: 'relative', zIndex: 1 }}>
            <Button
              href="/jee-results-2026/"
              variant="contained"
              size="large"
              sx={{
                borderRadius: 1,
                textTransform: 'none',
                fontWeight: 700,
                px: 3,
              }}
            >
              View JEE Main Results
            </Button>
          </Box>
        </HeaderSection>

        <Box sx={{ px: { xs: 1.5, sm: 2, md: 3, lg: 6 }, mb: 5 }}>
          <Grid
            container
            spacing={{ xs: 2, sm: 2.5, md: 3, lg: 4 }}
            sx={{
              justifyContent: 'center',
              display: 'flex',
              width: '100%',
            }}
          >
            {renderStudentCard(topStudent)}
          </Grid>
        </Box>

        <Box sx={{ px: { xs: 1.5, sm: 2, md: 3, lg: 6 }, pb: 8 }}>
          <Grid
            container
            spacing={{ xs: 2, sm: 2.5, md: 3, lg: 4 }}
            sx={{
              justifyContent: 'center',
              display: 'flex',
              width: '100%',
            }}
          >
            {otherStudents.map((student) => renderStudentCard(student))}
          </Grid>
        </Box>
      </Box>
    </Main>
  );
};

export default JeeAdvancedResults2026;
